import { useState, useEffect } from "react";
import posthog from "../../lib/posthog";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

interface DiagnosticResult {
  check: string;
  status: string;
  details: string;
}

interface DiagnosticsResponse {
  overall_status: string;
  commit: string;
  timestamp: string;
  results: DiagnosticResult[];
}

export default function AppDiagnostics() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DiagnosticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dbCounter, setDbCounter] = useState<number | null>(null);
  const [flagState, setFlagState] = useState<boolean | null>(null);
  const [apiInfo, setApiInfo] = useState<{
    version: string;
    commit: string;
    environment: string;
  } | null>(null);

  const fetchApiInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/version`);
      if (res.ok) {
        const info = await res.json();
        setApiInfo(info);
      }
    } catch (e) {
      console.error("Failed to fetch API version info:", e);
    }
  };

  const checkFeatureFlag = () => {
    // PostHog evaluation (gated client-side)
    const isEnabled = posthog.isFeatureEnabled("ff-diagnostics-test") || false;
    setFlagState(isEnabled);
  };

  useEffect(() => {
    fetchApiInfo();
    checkFeatureFlag();
    // Re-check flag periodically as it changes on server side
    const interval = setInterval(checkFeatureFlag, 5000);
    return () => clearInterval(interval);
  }, []);

  const runAllChecks = async () => {
    setLoading(true);
    setError(null);
    try {
      posthog.capture("run_diagnostics_started", {
        timestamp: new Date().toISOString(),
      });
      const res = await fetch(`${API_URL}/diagnostics/run`);
      if (!res.ok) throw new Error(`HTTP Error ${res.status}`);
      const json: DiagnosticsResponse = await res.json();
      setData(json);

      // Parse counter state from details if present
      const dbCheck = json.results.find(
        (r) => r.check === "supabase_read_write",
      );
      if (dbCheck && dbCheck.status === "PASS") {
        const match = dbCheck.details.match(/to (\d+)\./);
        if (match) setDbCounter(parseInt(match[1]));
      }

      posthog.capture("run_diagnostics_completed", {
        overall_status: json.overall_status,
      });
    } catch (e: any) {
      setError(e.message || "Connection to API failed.");
      posthog.capture("run_diagnostics_failed", { error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const triggerHandledError = () => {
    try {
      posthog.capture("handled_error_triggered");
      throw new Error("Simulated frontend handled exception (Phase 0 check)");
    } catch (e: any) {
      console.warn(e);
      // Capture error in PostHog
      posthog.capture("$exception", {
        $exception_message: e.message,
        $exception_type: "Error",
        $exception_handled: true,
      });
      alert("Handled warning captured and sent to PostHog!");
    }
  };

  const triggerUnhandledError = async () => {
    posthog.capture("unhandled_error_triggered");
    try {
      const res = await fetch(`${API_URL}/diagnostics/error`, {
        method: "POST",
      });
      const json = await res.json();
      alert(`Server Response: ${JSON.stringify(json)}`);
    } catch (e) {
      alert(
        "Backend error API called successfully (Server logged 500 Internal Server Error). Check Railway logs.",
      );
    }
  };

  const manualDbIncrement = async () => {
    console.log("Increment button clicked. API_URL:", API_URL);
    try {
      const res = await fetch(`${API_URL}/diagnostics/increment`, {
        method: "POST",
        headers: {
          "Accept": "application/json"
        }
      });
      console.log("Increment response status:", res.status);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      const json = await res.json();
      console.log("Increment response JSON:", json);
      if (json && typeof json.count === "number") {
        setDbCounter(json.count);
        posthog.capture("manual_db_increment", { new_count: json.count });
      } else {
        throw new Error("Invalid response format: 'count' not found or not a number");
      }
    } catch (e: any) {
      console.error("DB increment request failed:", e);
      alert(`DB increment request failed: ${e.message || e}`);
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.glassCard}>
          <h1 style={styles.title}>🌌 Huddlezen v2</h1>
          <p style={styles.subtitle}>
            Infrastructure Diagnostics & Control Panel [Phase 0]
          </p>
        </div>
      </header>

      <main style={styles.main}>
        {/* Controls Section */}
        <section style={styles.section}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>🔬 Test Diagnostics Control</h2>
            <div style={styles.btnGroup}>
              <button
                onClick={runAllChecks}
                disabled={loading}
                style={{ ...styles.btn, ...styles.btnPrimary }}
              >
                {loading ? "Running Tests..." : "⚡ Run Full Diagnostics"}
              </button>
              <button
                onClick={manualDbIncrement}
                style={{ ...styles.btn, ...styles.btnSecondary }}
              >
                ➕ Increment Database Counter
              </button>
              <button
                onClick={triggerHandledError}
                style={{ ...styles.btn, ...styles.btnWarning }}
              >
                ⚠️ Trigger Handled Error
              </button>
              <button
                onClick={triggerUnhandledError}
                style={{ ...styles.btn, ...styles.btnDanger }}
              >
                💥 Trigger Simulated Crash (500)
              </button>
            </div>
          </div>
        </section>

        {/* Status Indicators */}
        <section style={styles.grid}>
          {/* Environment/Build Details */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>ℹ️ Environment & Build Info</h3>
            <table style={styles.table}>
              <tbody>
                <tr>
                  <td style={styles.tdLabel}>App Origin:</td>
                  <td style={styles.tdVal}>{window.location.origin}</td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>API URL:</td>
                  <td style={styles.tdVal}>{API_URL}</td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>API Environment:</td>
                  <td style={styles.tdVal}>
                    {apiInfo?.environment || "checking..."}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>API Version:</td>
                  <td style={styles.tdVal}>
                    {apiInfo?.version || "checking..."}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Commit Hash:</td>
                  <td style={{ ...styles.tdVal, ...styles.code }}>
                    {apiInfo?.commit || "checking..."}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Integration Statuses */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🔌 Integration Live Status</h3>
            <table style={styles.table}>
              <tbody>
                <tr>
                  <td style={styles.tdLabel}>Supabase DB State:</td>
                  <td style={styles.tdVal}>
                    {dbCounter !== null ? (
                      <span style={styles.badgeSuccess}>
                        Connected (Val: {dbCounter})
                      </span>
                    ) : (
                      <span style={styles.badgeWait}>
                        Waiting for test run...
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>PostHog Ingestion:</td>
                  <td style={styles.tdVal}>
                    {import.meta.env.VITE_POSTHOG_KEY && import.meta.env.VITE_POSTHOG_KEY !== "phc_placeholder" ? (
                      <span style={styles.badgeSuccess}>Loaded</span>
                    ) : (
                      <span style={styles.badgeDanger}>Not Configured</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>Feature Flag state:</td>
                  <td style={styles.tdVal}>
                    {flagState !== null ? (
                      flagState ? (
                        <span style={styles.badgeSuccess}>Enabled (ON)</span>
                      ) : (
                        <span style={styles.badgeWarning}>Disabled (OFF)</span>
                      )
                    ) : (
                      <span style={styles.badgeWait}>Fetching...</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={styles.tdLabel}>API Connectivity:</td>
                  <td style={styles.tdVal}>
                    {apiInfo ? (
                      <span style={styles.badgeSuccess}>Online</span>
                    ) : (
                      <span style={styles.badgeDanger}>
                        Offline / Connecting
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Diagnostics Results Output */}
        {(data || error) && (
          <section style={styles.section}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>
                📋 Diagnostics Run Results:
                {data && (
                  <span
                    style={
                      data.overall_status === "PASS"
                        ? styles.badgeSuccess
                        : styles.badgeDanger
                    }
                  >
                    {data.overall_status}
                  </span>
                )}
              </h3>

              {error && (
                <div style={styles.errorBox}>
                  Error executing diagnostics check: {error}
                </div>
              )}

              {data && (
                <div style={styles.resultsContainer}>
                  {data.results.map((r, i) => (
                    <div key={i} style={styles.resultItem}>
                      <div style={styles.resultHeader}>
                        <span style={styles.resultName}>{r.check}</span>
                        <span
                          style={
                            r.status === "PASS"
                              ? styles.badgeSuccess
                              : styles.badgeDanger
                          }
                        >
                          {r.status}
                        </span>
                      </div>
                      <div style={styles.resultDetails}>{r.details}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <footer style={styles.footer}>
        <p>
          Huddlezen rebuild initiative. Built by Antigravity in pairing mode.
        </p>
      </footer>
    </div>
  );
}

// Inline styles for glassmorphism / dark premium aesthetic
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#0a0a16",
    color: "#e2e8f0",
    fontFamily: "system-ui, -apple-system, sans-serif",
    padding: "2rem 1rem",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
  },
  header: {
    width: "100%",
    maxWidth: "1000px",
    marginBottom: "2rem",
  },
  glassCard: {
    background: "rgba(255, 255, 255, 0.03)",
    borderRadius: "16px",
    padding: "1.5rem 2rem",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    textAlign: "center" as const,
    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
  },
  title: {
    fontSize: "2.5rem",
    margin: 0,
    background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: 800,
    letterSpacing: "-0.05em",
  },
  subtitle: {
    margin: "0.5rem 0 0 0",
    color: "#94a3b8",
    fontSize: "1rem",
  },
  main: {
    width: "100%",
    maxWidth: "1000px",
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: "2rem",
  },
  section: {
    width: "100%",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1.5rem",
    width: "100%",
  },
  card: {
    backgroundColor: "#111126",
    borderRadius: "12px",
    padding: "1.5rem",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.2)",
  },
  cardTitle: {
    fontSize: "1.25rem",
    margin: "0 0 1rem 0",
    color: "#f8fafc",
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    paddingBottom: "0.5rem",
  },
  btnGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.75rem",
  },
  btn: {
    padding: "0.75rem 1rem",
    borderRadius: "8px",
    border: "none",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "0.95rem",
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #6366f1, #4f46e5)",
    color: "#ffffff",
  },
  btnSecondary: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    color: "#f8fafc",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  btnWarning: {
    backgroundColor: "rgba(245, 158, 11, 0.15)",
    color: "#f59e0b",
    border: "1px solid rgba(245, 158, 11, 0.3)",
  },
  btnDanger: {
    backgroundColor: "rgba(239, 68, 68, 0.15)",
    color: "#ef4444",
    border: "1px solid rgba(239, 68, 68, 0.3)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
  },
  tdLabel: {
    padding: "0.5rem 0",
    color: "#94a3b8",
    fontSize: "0.9rem",
    width: "40%",
  },
  tdVal: {
    padding: "0.5rem 0",
    color: "#f1f5f9",
    fontSize: "0.9rem",
    textAlign: "right" as const,
  },
  code: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: "#38bdf8",
  },
  badgeSuccess: {
    backgroundColor: "rgba(34, 197, 94, 0.15)",
    color: "#22c55e",
    padding: "0.25rem 0.6rem",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  badgeWarning: {
    backgroundColor: "rgba(245, 158, 11, 0.15)",
    color: "#f59e0b",
    padding: "0.25rem 0.6rem",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  badgeDanger: {
    backgroundColor: "rgba(239, 68, 68, 0.15)",
    color: "#ef4444",
    padding: "0.25rem 0.6rem",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  badgeWait: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    color: "#94a3b8",
    padding: "0.25rem 0.6rem",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  errorBox: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    color: "#fca5a5",
    padding: "0.75rem 1rem",
    borderRadius: "6px",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  },
  resultsContainer: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.75rem",
    marginTop: "1rem",
  },
  resultItem: {
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
  },
  resultHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.25rem",
  },
  resultName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: "#e2e8f0",
  },
  resultDetails: {
    fontSize: "0.85rem",
    color: "#94a3b8",
  },
  footer: {
    marginTop: "3rem",
    color: "#475569",
    fontSize: "0.85rem",
    textAlign: "center" as const,
  },
};
